import { api } from './api'

export interface SessionValidationResponse {
  valid: boolean
  timeRemaining?: number
  expiresAt?: string
}

export interface SessionInfo {
  sessionId: string
  userId: string
  email: string
  lastActivity: string
  sessionStart: string
  ip: string
  userAgent?: string
  isActive: boolean
}

export interface UserSessionsResponse {
  sessions: SessionInfo[]
  totalSessions: number
  maxSessions: number
}

export interface SessionExtensionResponse {
  success: boolean
  newExpiresAt?: string
  timeExtended?: number
}

/**
 * Session API Service
 * 
 * Handles all session-related API calls including:
 * - Session validation
 * - Session information retrieval
 * - User session management
 * - Session extension requests
 */
export const sessionApi = {
  /**
   * Validate current session with backend
   */
  async validateSession(): Promise<boolean> {
    try {
      const response = await api.get<SessionValidationResponse>('/user-sessions/validate')
      return response.data.valid
    } catch (error: any) {
      const status: number | undefined = error?.response?.status

      // Explicit 401 → session is genuinely expired.
      if (status === 401) {
        return false
      }

      // 503 means the session service is temporarily unavailable.
      // Re-throw so the caller (SessionManager) can apply exponential backoff,
      // but without logging — the 503 is an expected degraded-mode response.
      if (status === 503) {
        throw error
      }

      // Any other network/server error: re-throw so the caller can apply backoff.
      // SessionManager logs the warning and backs off automatically.
      throw error
    }
  },

  /**
   * Get detailed session validation with timing info
   */
  async getSessionValidation(): Promise<SessionValidationResponse> {
    try {
      const response = await api.get<SessionValidationResponse>('/user-sessions/validate')
      return response.data
    } catch (error) {
      console.error('Session validation failed:', error)
      return { valid: false }
    }
  },

  /**
   * Get current session information
   */
  async getCurrentSession(): Promise<SessionInfo | null> {
    try {
      const response = await api.get<SessionInfo>('/user-sessions/current')
      return response.data
    } catch (error) {
      console.error('Failed to get current session:', error)
      return null
    }
  },

  /**
   * Get all active sessions for the current user
   */
  async getUserSessions(): Promise<UserSessionsResponse> {
    try {
      const response = await api.get<UserSessionsResponse>('/user-sessions/user')
      return response.data
    } catch (error) {
      console.error('Failed to get user sessions:', error)
      return {
        sessions: [],
        totalSessions: 0,
        maxSessions: 3
      }
    }
  },

  /**
   * Extend current session
   */
  async extendSession(): Promise<SessionExtensionResponse> {
    try {
      const response = await api.post<SessionExtensionResponse>('/user-sessions/extend')
      return response.data
    } catch (error) {
      console.error('Failed to extend session:', error)
      return { success: false }
    }
  },

  /**
   * Terminate a specific session
   */
  async terminateSession(sessionId: string): Promise<boolean> {
    try {
      await api.delete(`/user-sessions/${sessionId}`)
      return true
    } catch (error) {
      console.error('Failed to terminate session:', error)
      return false
    }
  },

  /**
   * Terminate all sessions except current one
   */
  async terminateOtherSessions(): Promise<boolean> {
    try {
      await api.delete('/user-sessions/others')
      return true
    } catch (error) {
      console.error('Failed to terminate other sessions:', error)
      return false
    }
  },

  /**
   * Terminate all sessions (including current)
   */
  async terminateAllSessions(): Promise<boolean> {
    try {
      await api.delete('/user-sessions/all')
      return true
    } catch (error) {
      console.error('Failed to terminate all sessions:', error)
      return false
    }
  },

  /**
   * Get session statistics
   */
  async getSessionStats(): Promise<{
    totalSessions: number
    activeUsers: number
    averageSessionDuration: number
  }> {
    try {
      const response = await api.get<{
        totalSessions: number
        activeUsers: number
        averageSessionDuration: number
      }>('/user-sessions/stats')
      return response.data
    } catch (error) {
      console.error('Failed to get session stats:', error)
      return {
        totalSessions: 0,
        activeUsers: 0,
        averageSessionDuration: 0
      }
    }
  }
}
